import Header from 'components/Header';
import { Box, useTheme, Tabs, Tab } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { colorTheme } from '../../theme';
import EmployeeGrid from './employeeGrid';
import EmployerDashboard from './employerDashboard';
import AdminGrid from './adminGrid';
import { useSelector } from 'react-redux';
import { type RootState } from 'store';
import ContributionGrid from './contributionGrid';
import { useLocation, useNavigate } from 'react-router-dom';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps): JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, height: '75vh' }}>{children}</Box>}
    </div>
  );
}

const EmployerDetails = (): JSX.Element => {
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const { organizationInfo } = useSelector(
    (state: RootState) => state.employeeDetails,
  );
  const location = useLocation();
  const navigate = useNavigate();

  const tabFromSearch = useMemo(() => {
    const tabParam = Number(new URLSearchParams(location.search).get('tab'));
    return Number.isNaN(tabParam) ? 0 : tabParam;
  }, [location.search]);

  /* Tab Menu */
  const [value, setValue] = useState<number>(tabFromSearch);

  const handleChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ): void => {
    setValue(newValue);
    navigate(`${location.pathname}?tab=${newValue}`, { replace: true });
  };

  useEffect(() => {
    if (tabFromSearch !== value) {
      setValue(tabFromSearch);
    }
  }, [tabFromSearch, value]);

  return (
    <Box>
      <Box sx={{ display: 'flex' }}>
        <Header title={organizationInfo?.companyName} />
      </Box>
      <Box py={2} />
      <Box
        sx={{
          maxWidth: {
            xs: 320,
            sm: 480,
            md: '100%',
          },
          borderRadius: 3,
          px: 2,
          boxShadow: 2,
          bgcolor: 'background.paper',
        }}
      >
        <Tabs
          value={value}
          onChange={handleChange}
          variant="scrollable"
          indicatorColor="secondary"
          scrollButtons="auto"
          aria-label="scrollable auto tabs example"
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.greenAccent[600],
            },
          }}
          sx={{
            '& .MuiTab-root.Mui-selected': {
              color: colors.greenAccent[500],
            },
          }}
        >
          <Tab label="Employer Dashboard" />
          <Tab label="Admins" />
          <Tab label="Employees" />
          <Tab label="Contribution" />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <EmployerDashboard setValueChange={setValue} />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <AdminGrid />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <EmployeeGrid />
      </TabPanel>
      <TabPanel value={value} index={3}>
        <ContributionGrid />
      </TabPanel>
    </Box>
  );
};

export default EmployerDetails;
